// mds-dojo — dojo/request/xhr. fetch-based, with dojo's request/response semantics
// (ported for the real webtool RequestManager, which POSTs form-encoded data to
// Data/Callfunction and probes the returned promise like a dojo promise):
//   * options.data OBJECT → application/x-www-form-urlencoded body (dojo form-encode;
//     the C# side reads form fields). String/FormData bodies pass through.
//   * GET/HEAD/DELETE data + options.query → appended to the URL query string.
//   * preventCache:true → dojo.preventCache=<ts> query param (dojo parity).
//   * default header X-Requested-With: XMLHttpRequest (dojo sets it; server code may key on it).
//   * non-2xx → REJECT with a RequestError carrying .response {status, text, data} —
//     dojo rejects error statuses; handlers still run so .data is populated when parseable.
//   * cancel() → REJECT with err.name === "CancelError" (dojo's name; RequestManager
//     swallows those silently).
//   * the promise exposes isResolved()/isRejected()/isFulfilled()/isCanceled() — the dojo
//     promise surface RequestManager probes before calling .cancel().
//   * sync:true → synchronous XMLHttpRequest (legacy escape hatch; avoid in new code).
//   * timeout (ms) → cancel with err.name "RequestTimeoutError" (dojo parity).

import handlers from './handlers';

interface XhrOptions {
  handleAs?: string;
  method?: string;
  preventCache?: boolean;
  data?: any;
  query?: any;
  headers?: Record<string, string>;
  sync?: boolean;
  timeout?: number;
}

function toQuery(obj: any): string {
  const parts: string[] = [];
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === undefined) { continue; }
    if (Array.isArray(v)) {
      for (const item of v) { parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(item == null ? '' : item)); }
    } else {
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(v == null ? '' : v));
    }
  }
  return parts.join('&');
}

function appendQuery(url: string, qs: string): string {
  if (!qs) { return url; }
  return url + (url.indexOf('?') === -1 ? '?' : '&') + qs;
}

function makeError(name: string, message: string, response: any): Error {
  const err: any = new Error(message);
  err.name = name;
  err.response = response;
  err.status = response && response.status;
  return err;
}

function buildResponse(url: string, options: XhrOptions, status: number, text: string) {
  return { url, options, status, text, data: undefined as any, getHeader: (_n: string) => null as any };
}

function finishResponse(response: any, options: XhrOptions): any {
  // Run the handleAs handler even on error statuses (dojo does), so error
  // envelopes that are still JSON get a parsed .data for the caller.
  let value: any;
  try {
    value = handlers.apply(response, options.handleAs || 'text');
  } catch (e) {
    if (response.status >= 200 && response.status < 300) { throw e; }
    value = undefined;
  }
  if (response.data === undefined) { response.data = value; }
  if (response.status >= 200 && response.status < 300) { return value; }
  throw makeError('RequestError', 'Unable to load ' + response.url + ' status: ' + response.status, response);
}

export default function xhr(url: string, options?: XhrOptions): Promise<any> & {
  cancel?: () => void;
  isResolved?: () => boolean;
  isRejected?: () => boolean;
  isFulfilled?: () => boolean;
  isCanceled?: () => boolean;
} {
  options = options || {};
  const method = (options.method || 'GET').toUpperCase();
  const bodyless = method === 'GET' || method === 'HEAD';

  // --- URL assembly: query option, bodyless data, preventCache --------------
  if (options.query) {
    url = appendQuery(url, typeof options.query === 'string' ? options.query : toQuery(options.query));
  }
  if (options.data != null && bodyless) {
    url = appendQuery(url, typeof options.data === 'string' ? options.data : toQuery(options.data));
  }
  if (options.preventCache) {
    url = appendQuery(url, 'dojo.preventCache=' + Date.now());
  }

  // --- headers + body --------------------------------------------------------
  const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
  for (const k of Object.keys(options.headers || {})) {
    const v = (options.headers as any)[k];
    if (v != null) { headers[k] = v; }
  }
  let body: any;
  if (options.data != null && !bodyless) {
    if (typeof options.data === 'string' ||
        (typeof FormData !== 'undefined' && options.data instanceof FormData) ||
        (typeof Blob !== 'undefined' && options.data instanceof Blob)) {
      body = options.data;
    } else {
      body = toQuery(options.data);
      if (!Object.keys(headers).some((h) => h.toLowerCase() === 'content-type')) {
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
    }
  }

  // --- sync escape hatch (XMLHttpRequest) ------------------------------------
  if (options.sync) {
    const req = new XMLHttpRequest();
    req.open(method, url, false);
    for (const k of Object.keys(headers)) { req.setRequestHeader(k, headers[k]); }
    req.send(body != null ? body : null);
    const response = buildResponse(url, options, req.status, req.responseText);
    const ok = req.status >= 200 && req.status < 300;
    const decorateSync = (p: any): any => {
      p.cancel = () => { /* already finished */ };
      p.isResolved = () => ok;
      p.isRejected = () => !ok;
      p.isFulfilled = () => true;
      p.isCanceled = () => false;
      const nativeThen = p.then.bind(p);
      const nativeCatch = p.catch.bind(p);
      p.then = (okCb?: any, errCb?: any) => decorateSync(nativeThen(okCb, errCb));
      p.catch = (errCb?: any) => decorateSync(nativeCatch(errCb));
      return p;
    };
    return decorateSync(Promise.resolve().then(() => finishResponse(response, options!)));
  }

  // --- async fetch ------------------------------------------------------------
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let state: 'pending' | 'resolved' | 'rejected' | 'canceled' | 'timeout' = 'pending';
  let timer: any = null;

  const fetched = fetch(url, {
    method,
    headers,
    body,
    signal: controller ? controller.signal : undefined,
    credentials: 'same-origin',
  }).then(
    (res) => res.text().then((text) => {
      if (timer) { clearTimeout(timer); }
      const response = buildResponse(url, options!, res.status, text);
      const value = finishResponse(response, options!);
      state = 'resolved';
      return value;
    }),
    (e) => {
      if (timer) { clearTimeout(timer); }
      if (state === 'canceled' || (e && e.name === 'AbortError')) {
        const err = makeError(state === 'timeout' ? 'RequestTimeoutError' : 'CancelError',
          state === 'timeout' ? 'Timeout exceeded' : 'Request canceled', buildResponse(url, options!, 0, ''));
        throw err;
      }
      state = 'rejected';
      throw e;
    },
  ).catch((e) => {
    if (state === 'pending') { state = 'rejected'; }
    throw e;
  });

  if (options.timeout && controller) {
    timer = setTimeout(() => { state = 'timeout'; controller.abort(); }, options.timeout);
  }

  // Decorate with the dojo promise surface — and KEEP it across .then()/.catch()
  // chains: RequestManager stores the CHAINED promise (`xhr(...).then(ok, err)`)
  // and later probes `request.isResolved()` / calls `request.cancel()` on it.
  // dojo/promise chains delegate cancel to the root request; we do the same.
  function decorate(p: any): any {
    p.cancel = () => {
      if (state === 'pending' && controller) { state = 'canceled'; controller.abort(); }
    };
    p.isResolved = () => state === 'resolved';
    p.isRejected = () => state === 'rejected';
    p.isFulfilled = () => state !== 'pending';
    p.isCanceled = () => state === 'canceled' || state === 'timeout';
    const nativeThen = p.then.bind(p);
    const nativeCatch = p.catch.bind(p);
    p.then = (ok?: any, err?: any) => decorate(nativeThen(ok, err));
    p.catch = (err?: any) => decorate(nativeCatch(err));
    return p;
  }
  return decorate(fetched);
}
